import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiCheckCircle,
    FiClock,
    FiTrendingUp,
    FiShield,
    FiArrowLeft,
    FiArrowRight,
    FiActivity,
    FiCpu,
    FiUserCheck,
    FiLayers,
    FiAward,
    FiChevronDown,
    FiChevronUp,
} from "react-icons/fi";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/Roadmap-fa.json";
import en from "./locales/Roadmap-en.json";
import ar from "./locales/Roadmap-ar.json";
import tr from "./locales/Roadmap-tr.json";

interface TimelineItem {
    phase: string;
    title: string;
    description: string;
    details: string;
    status: string;
    progress: number;
}

const Roadmap: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "Roadmap";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [expandedItem, setExpandedItem] = useState<number | null>(null);
    const [hoveredItem, setHoveredItem] = useState<number | null>(null);

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";

    const timelineItems = Array.from({ length: 4 }, (_, i) => {
        const item = t(`timeline.${i}`, { ns: "Roadmap", returnObjects: true, defaultValue: null }) as any;
        if (item && item.phase) return item as TimelineItem;
        return null;
    }).filter(Boolean) as TimelineItem[];

    const achievements = Array.from({ length: 12 }, (_, i) => {
        const a = t(`achievements.${i}`, { ns: "Roadmap", returnObjects: true, defaultValue: null }) as any;
        if (a && a.title) return a;
        return null;
    }).filter(Boolean) as Array<{ title: string; desc: string }>;

    // Split achievements into 2 columns for dual-scrolling
    const col1Base = achievements.filter((_, i) => i % 2 === 0);
    const col2Base = achievements.filter((_, i) => i % 2 === 1);

    // Duplicate arrays to create seamless infinite loop
    const col1 = [...col1Base, ...col1Base, ...col1Base];
    const col2 = [...col2Base, ...col2Base, ...col2Base];

    const getPhaseIcon = (index: number) => {
        switch (index) {
            case 0:
                return <FiUserCheck className="w-5 h-5 text-[#0072CE]" />;
            case 1:
                return <FiActivity className="w-5 h-5 text-[#0072CE]" />;
            case 2:
                return <FiCpu className="w-5 h-5 text-[#0072CE]" />;
            case 3:
                return <FiLayers className="w-5 h-5 text-[#0072CE]" />;
            default:
                return <FiTrendingUp className="w-5 h-5 text-[#0072CE]" />;
        }
    };

    const getStatusStyle = (status: string) => {
        if (status.includes("تکمیل") || status.toLowerCase().includes("complete")) {
            return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" };
        }
        if (status.includes("ریدیزاین") || status.toLowerCase().includes("redesign") || status.includes("توسعه")) {
            return { bg: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-[#0072CE]" };
        }
        if (status.includes("تحقیق") || status.toLowerCase().includes("research")) {
            return { bg: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" };
        }
        return { bg: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" };
    };

    const getPhaseCapabilities = (index: number) => {
        if (isRTL) {
            switch (index) {
                case 0:
                    return [
                        "ساعت هوشمند کودک با پایش GPS",
                        "تعریف حصار امنیتی دیجیتال (Geofencing)",
                        "دکمه و سیستم هشدار اضطراری SOS",
                        "اپلیکیشن اختصاصی نظارت والدین",
                    ];
                case 1:
                    return [
                        "پایش مداوم اکسیژن خون و ضربان قلب (SpO2 & HR)",
                        "ثبت نوار قلب و تحلیل الکتروکاردیوگرام (ECG)",
                        "تحلیل هوشمند الگوی خواب و استرس",
                        "سنسور مداوم سنجش دمای پوستی",
                    ];
                case 2:
                    return [
                        "ارائه راهکارهای اختصاصی و پیش‌بینی هوش مصنوعی سلامت",
                        "مود ورزشی پیشرفته، سنجش بار تمرینی و ریکاوری",
                        "پایش هوشمند چرخه زیستی و سلامت دیجیتال بانوان",
                        "توصیه‌های شخصی‌سازی‌شده سبک زندگی و تندرستی",
                    ];
                case 3:
                    return [
                        "پنل و داشبورد سلامت دیجیتال سازمانی و ایمنی (HSE)",
                        "راهکارهای جامع سلامت شغلی و پایش تندرستی کارکنان",
                        "تحلیل ارگونومی محیط کار و پیشگیری از فرسودگی شغلی",
                        "یکپارچه‌سازی با زیرساخت‌های منابع انسانی و گزارش‌گیری",
                    ];
                default:
                    return ["پایش هوشمند", "گزارش‌گیری آنلاین", "سیستم هشدار"];
            }
        } else {
            switch (index) {
                case 0:
                    return [
                        "Smart kid watch with GPS tracking",
                        "Digital Geofence security boundaries",
                        "SOS emergency alert system",
                        "Parental control mobile app",
                    ];
                case 1:
                    return [
                        "Continuous SpO2 & Heart Rate monitoring",
                        "ECG recording & signal analysis",
                        "Sleep quality & stress tracking",
                        "Continuous skin temperature sensor",
                    ];
                case 2:
                    return [
                        "Custom AI health analytics & predictive models",
                        "Dedicated workout mode, strain & recovery tracking",
                        "Smart women's digital health & hormonal cycle insights",
                        "Personalized wellness & lifestyle recommendations",
                    ];
                case 3:
                    return [
                        "Enterprise digital health & HSE workforce dashboard",
                        "Comprehensive occupational health solutions",
                        "Ergonomics analysis & workplace burnout prevention",
                        "Seamless HR platform integration & analytics",
                    ];
                default:
                    return ["Smart Monitoring", "Real-time Reports", "Alert System"];
            }
        }
    };

    const scrollToContact = () => {
        const el = document.getElementById("contact");
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <section dir={dir} id="roadmap" className="pb-32 relative">
            <div className="container mx-auto px-4 sm:px-10">
                <SectionHeader
                    regularText={t("sectionHeader.regularText", { ns: "Roadmap" })}
                    highlightedText={t("sectionHeader.highlightedText", { ns: "Roadmap" })}
                    descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "Roadmap" })}
                    descriptionText={t("sectionHeader.descriptionText", { ns: "Roadmap" })}
                    className="mb-12"
                />

                <div className="flex flex-col lg:flex-row gap-10 xl:gap-14 items-start">
                    {/* Left Column: Partnership, Achievements & APN */}
                    <motion.div
                        className="w-full lg:w-5/12 space-y-6"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                    >
                        {/* MCI Partnership Banner */}
                        <div className="bg-white/90 backdrop-blur-sm p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs">
                            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                                <img
                                    src="/media/ogo-mci.webp"
                                    alt={t("partnership.title", { ns: "Roadmap" })}
                                    className="h-8 sm:h-10 w-auto object-contain"
                                />
                                <div className="h-5 sm:h-6 w-px bg-slate-200"></div>
                                <h3 className="text-base sm:text-xl font-bold text-slate-800">
                                    {t("partnership.title", { ns: "Roadmap" })}
                                </h3>
                            </div>
                            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                                {t("partnership.description", { ns: "Roadmap" })}
                            </p>
                        </div>

                        {/* Inline Keyframes style for achievements auto-scroll */}
                        <style>{`
                            @keyframes marqueeScrollDown {
                                0% { transform: translateY(-50%); }
                                100% { transform: translateY(0%); }
                            }
                            @keyframes marqueeScrollUp {
                                0% { transform: translateY(0%); }
                                100% { transform: translateY(-50%); }
                            }
                            .achievement-scroll-down {
                                animation: marqueeScrollDown 38s linear infinite;
                            }
                            .achievement-scroll-up {
                                animation: marqueeScrollUp 38s linear infinite;
                            }
                        `}</style>

                        {/* Dual Column Auto-Scrolling Achievements */}
                        <div className="bg-white/80 backdrop-blur-sm p-3.5 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs space-y-3">
                            <div className="flex items-center justify-between px-1">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                        <FiAward className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </div>
                                    <h4 className="font-bold text-slate-800 text-xs sm:text-base">
                                        {isRTL ? "دستآوردها و افتخارات دل‌بند" : "Delband Achievements & Honors"}
                                    </h4>
                                </div>
                            </div>

                            {/* Dual Scrolling Box */}
                            <div className="relative overflow-hidden rounded-2xl h-[260px] sm:h-[420px] bg-slate-50/70 p-2 sm:p-2.5 border border-slate-200/70 group">
                                {/* Top and Bottom Fade Gradients */}
                                <div className="absolute top-0 inset-x-0 h-8 sm:h-10 bg-gradient-to-b from-slate-100 via-slate-100/60 to-transparent z-20 pointer-events-none" />
                                <div className="absolute bottom-0 inset-x-0 h-8 sm:h-10 bg-gradient-to-t from-slate-100 via-slate-100/60 to-transparent z-20 pointer-events-none" />

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 h-full">
                                    {/* Column 1: Right Column (RTL) - Scrolls Downward */}
                                    <div className="overflow-hidden relative h-full">
                                        <div className="space-y-2.5 sm:space-y-3 flex flex-col achievement-scroll-down group-hover:[animation-play-state:paused]">
                                            {col1.map((item, idx) => (
                                                <div
                                                    key={`col1-${idx}`}
                                                    className="bg-white/95 backdrop-blur-md p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-2xs hover:border-blue-400 hover:shadow-md transition-all duration-300 shrink-0 cursor-default"
                                                >
                                                    <div className="flex items-start gap-2 mb-1">
                                                        <FiAward className="w-3.5 h-3.5 text-[#0072CE] shrink-0 mt-0.5" />
                                                        <h5 className="font-bold text-slate-900 text-xs sm:text-sm leading-snug">
                                                            {item.title}
                                                        </h5>
                                                    </div>
                                                    <p className="text-slate-600 text-[11px] sm:text-xs leading-relaxed">
                                                        {item.desc}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Column 2: Left Column (RTL) - Scrolls Upward on sm+ */}
                                    <div className="hidden sm:block overflow-hidden relative h-full">
                                        <div className="space-y-3 flex flex-col achievement-scroll-up group-hover:[animation-play-state:paused]">
                                            {col2.map((item, idx) => (
                                                <div
                                                    key={`col2-${idx}`}
                                                    className="bg-white/95 backdrop-blur-md p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 shadow-2xs hover:border-blue-400 hover:shadow-md transition-all duration-300 shrink-0 cursor-default"
                                                >
                                                    <div className="flex items-start gap-2 mb-1.5">
                                                        <FiAward className="w-3.5 h-3.5 text-[#0072CE] shrink-0 mt-0.5" />
                                                        <h5 className="font-bold text-slate-900 text-xs sm:text-sm leading-snug">
                                                            {item.title}
                                                        </h5>
                                                    </div>
                                                    <p className="text-slate-600 text-[11px] sm:text-xs leading-relaxed">
                                                        {item.desc}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Dedicated APN Security Card */}
                        <motion.div
                            className="bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] rounded-2xl sm:rounded-3xl p-4 sm:p-7 text-white shadow-xl shadow-blue-500/20 relative overflow-hidden cursor-pointer"
                            whileHover={{ y: -3 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="relative z-10">
                                <div className="flex items-center gap-2.5 sm:gap-3 mb-2 sm:mb-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white shrink-0">
                                        <FiShield className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </div>
                                    <h4 className="font-bold text-sm sm:text-lg leading-snug text-white">
                                        {t("apn.title", { ns: "Roadmap" })}
                                    </h4>
                                </div>
                                <p className="text-white/90 text-xs sm:text-sm leading-relaxed">
                                    <span className="font-extrabold text-white underline decoration-white/40 underline-offset-4">
                                        {t("apn.highlight1", { ns: "Roadmap" })}
                                    </span>{" "}
                                    {t("apn.description", { ns: "Roadmap" })}
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Right Column: Timeline */}
                    <div className="w-full lg:w-7/12 relative">
                        {/* Vertical Timeline Track Line */}
                        <div
                            className={`absolute ${
                                isRTL ? "right-4.5 sm:right-6" : "left-4.5 sm:left-6"
                            } top-6 bottom-6 w-0.5 bg-gradient-to-b from-[#0072CE] via-[#0072CE]/40 to-slate-200 z-0`}
                        ></div>

                        <div className="space-y-4 sm:space-y-8 relative z-10">
                            {timelineItems.map((item, index) => {
                                const statusStyle = getStatusStyle(item.status);
                                const isExpanded = expandedItem === index;

                                return (
                                    <motion.div
                                        key={index}
                                        className="flex items-start gap-3 sm:gap-6"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: index * 0.08 }}
                                        viewport={{ once: true }}
                                        onMouseEnter={() => setHoveredItem(index)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        {/* Timeline Node Icon */}
                                        <div className="relative z-10 shrink-0 mt-1">
                                            <div
                                                className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center border transition-all duration-300 shadow-xs cursor-pointer ${
                                                    hoveredItem === index || isExpanded
                                                        ? "bg-[#0072CE] text-white border-[#0072CE] shadow-md shadow-blue-500/30 scale-105"
                                                        : "bg-white text-slate-700 border-slate-200/80"
                                                }`}
                                                onClick={() => setExpandedItem(isExpanded ? null : index)}
                                            >
                                                {React.cloneElement(getPhaseIcon(index), {
                                                    className: `w-4 h-4 sm:w-5 sm:h-5 ${
                                                        hoveredItem === index || isExpanded ? "text-white" : "text-[#0072CE]"
                                                    }`,
                                                })}
                                            </div>
                                        </div>

                                        {/* Timeline Card */}
                                        <div
                                            className={`flex-1 bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-2xl border transition-all duration-300 cursor-pointer ${
                                                isExpanded
                                                    ? "border-blue-400/90 shadow-lg shadow-blue-500/10 ring-2 ring-blue-500/10"
                                                    : "border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-md"
                                            }`}
                                            onClick={() => setExpandedItem(isExpanded ? null : index)}
                                        >
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                                    <span className="text-[10px] sm:text-xs font-bold text-[#0072CE] bg-blue-50 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md border border-blue-100 shrink-0">
                                                        {item.phase}
                                                    </span>
                                                    <h3 className="text-xs sm:text-lg font-bold text-slate-800">
                                                        {item.title}
                                                    </h3>
                                                </div>

                                                {/* Status Pill */}
                                                <div
                                                    className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border text-[10px] sm:text-xs font-bold shrink-0 ${statusStyle.bg}`}
                                                >
                                                    <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${statusStyle.dot}`}></span>
                                                    <span>{item.status}</span>
                                                </div>
                                            </div>

                                            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                                                {item.description}
                                            </p>

                                            {/* Progress Bar */}
                                            <div className="space-y-1 sm:space-y-1.5 mb-2.5 sm:mb-3">
                                                <div className="flex justify-between text-[10px] sm:text-[11px] font-bold text-slate-500">
                                                    <span>{t("labels.developmentStatus", { ns: "Roadmap" })}</span>
                                                    <span className="text-[#0072CE]">{item.progress}%</span>
                                                </div>
                                                <div className="w-full bg-slate-100 rounded-full h-1.5 sm:h-2 overflow-hidden border border-slate-200/60">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] rounded-full transition-all duration-700"
                                                        style={{ width: `${item.progress}%` }}
                                                    ></div>
                                                </div>
                                            </div>

                                            {/* Expandable Content Panel */}
                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.35, ease: "easeInOut" }}
                                                        className="overflow-hidden pt-3 sm:pt-4 mt-3 sm:mt-4 border-t border-slate-100 space-y-3 sm:space-y-4"
                                                    >
                                                        {/* Vision & Details */}
                                                        <div className="bg-gradient-to-br from-blue-50/70 to-slate-50/80 p-3 sm:p-4 rounded-xl border border-blue-100/80">
                                                            <div className="flex items-center gap-2 text-[#0072CE] font-bold text-[11px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">
                                                                <FiTrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                                <span>{isRTL ? "چشم‌انداز و جزئیات توسعه" : "Development Details & Vision"}</span>
                                                            </div>
                                                            <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">
                                                                {item.details}
                                                            </p>
                                                        </div>

                                                        {/* Key Capabilities */}
                                                        <div>
                                                            <h4 className="text-xs sm:text-sm font-bold text-slate-800 mb-2 sm:mb-2.5 flex items-center gap-2">
                                                                <FiCheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0072CE]" />
                                                                <span>{isRTL ? "قابلیت‌ها و دستاوردهای کلیدی این فاز" : "Key Capabilities & Features"}</span>
                                                            </h4>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                                                                {getPhaseCapabilities(index).map((cap, cIdx) => (
                                                                    <div
                                                                        key={cIdx}
                                                                        className="flex items-start gap-2 p-2 sm:p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-[11px] sm:text-xs text-slate-700 font-medium"
                                                                    >
                                                                        <FiCheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#0072CE] shrink-0 mt-0.5" />
                                                                        <span>{cap}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>

                                            {/* Footer Expand Trigger */}
                                            <div className="pt-2 sm:pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] sm:text-xs font-bold text-[#0072CE] hover:text-[#0038A8] transition-colors mt-2">
                                                <span>
                                                    {isExpanded
                                                        ? (isRTL ? "بستن جزئیات" : "Hide Details")
                                                        : (isRTL ? "مشاهده جزئیات بیشتر" : "More Details")}
                                                </span>
                                                {isExpanded ? (
                                                    <FiChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                ) : (
                                                    <FiChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Roadmap;
